import React, {useState} from 'react';
import {
  Button,
  FlexItem, FormField, FormFieldHelperText, FormFieldLabel, Input, Spinner, StackLayout
} from '@salt-ds/core';

import PageNavigation from '../../components/common/page-navigation/PageNavigation';
import '../CommonCard.css';
import Constants from '../../components/common/Constants';
import {useNavigate} from 'react-router';
import {getAccessCode, verifyAccessCode} from '../../api/ForgotPasswordApi';
import CardAlert from "../../components/common/card-alert/CardAlert";

function ForgotPasswordTwoStepVerificationForm(props) {
  const navigate = useNavigate();

  const [accessCode, setAccessCode] = useState("");
  const [error, setError] = useState({accessCodeError: {required: false, invalid: false}});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Function to handle button click
  const handleSendAccessCode = async () => {
    setIsSubmitting(true);
    props.setIsLoading(true);
    props.setHeaderContent(<Spinner aria-label="loading" role="status" size="medium" style={{marginLeft: "50%"}}/>);
    props.setShowBanner(false);

    try {
      const response = await getAccessCode(props.formData.orgId,props.formData.userId,props.formData.email);
      const delayPromise = new Promise((resolve) => setTimeout(resolve, 1000));
      if (response.error && response.error.detail) {
        setAccessCode("");
        props.showErrorOnPage(response.error.detail);
        setIsSubmitting(false);
      }
      await Promise.all([response, delayPromise]);


      props.setBannerText(Constants.forgotPasswordAccessCodeSentMessage);
      props.setStatusIcon("success");
      sessionStorage.setItem('userId', response.data.userId);
      sessionStorage.setItem('accessResourceToken', response.data.accessResourceToken);
    }
    catch{
      props.setBannerText("Error! try again");
      props.setStatusIcon("error");
      setIsSubmitting(false);
    }
    finally{
      props.setIsLoading(false);
      props.setShowBanner(true);
      props.setHeaderContent(<CardAlert status={props.statusIcon} setShowBanner={props.setShowBanner}>
        {props.bannerText}
      </CardAlert>);
      setIsSubmitting(false);
    }
  };

  const handleChange = (event) => {
    const {name, value} = event.target;
    setAccessCode(value);

    if (name === 'accessCode') {
      setError((prevErrors) => ({
        ...prevErrors,
        accessCodeError: {required: false, invalid: false},
      }));
    }
  };

  const handleSubmit = async(event) => {
    setIsSubmitting(true);
    const accessToken =sessionStorage.getItem('accessResourceToken');
    const userId = sessionStorage.getItem('userId');
    event.preventDefault();
    //Validation for empty fields
    const newErrors = {
      accessCodeError: {
        required: accessCode === '',
        invalid: accessCode !== '' && false,// Initially set to false; will be updated based on API response
      }
    };
    setError(newErrors);

    if (!newErrors.accessCodeError.required && !newErrors.accessCodeError.invalid ) {
      // Show spinner in banner area
      props.setIsLoading(true);
      props.setHeaderContent(
          <Spinner aria-label="loading" role="status" size="medium" style={{marginLeft: "50%"}}/>
      );
      props.setShowBanner(false);

      let isInvalidCode = false;
      try {
        const response = await verifyAccessCode(userId, accessCode, accessToken);

        if (response.data) {
          navigate(Constants.forgotPasswordComplete,{state:{from:Constants.forgotPasswordProvideAccessCode}});
        }
        else {
          props.setIsLoading(false);
          props.setShowBanner(false);
          setError((prevErrors) => ({
            ...prevErrors,
            accessCodeError: {required: false, invalid: true},
          }));
          setAccessCode("");
          isInvalidCode = true;
          setIsSubmitting(false); // <-- Ensure form is re-enabled after invalid code
        }
      } catch {
        props.setIsLoading(false);
        props.setShowBanner(false);
        setAccessCode("");
        isInvalidCode = true;
        setIsSubmitting(false);
      } finally {
        // Restore banner after API call only if not invalid code
        if (!isInvalidCode) {
          props.setShowBanner(true);
          props.setHeaderContent(
              <CardAlert status={props.statusIcon} setShowBanner={props.setShowBanner}>
                {props.bannerText}
              </CardAlert>
          );
        }
      }
      props.setIsLoading(false);
    } else {
      setIsSubmitting(false);
    }
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit} data-testid="forgot-password-two-step-form">
      <fieldset disabled={isSubmitting} style={{border:0, padding:0, margin:0}}>
      <input type="hidden" name="deviceSignature" id="deviceSignature" />
      <StackLayout gap={3}>
        <FlexItem className="form-layout">
          <FormField validationStatus={(error.accessCodeError.required || error.accessCodeError.invalid) ? "error" : undefined}>
            <FormFieldLabel>Verification Code</FormFieldLabel>
            <Input inputProps={{name: "accessCode", "aria-required": "true", "aria-describedby": (error.accessCodeError.required || error.accessCodeError.invalid) ? "accessCode-error" : undefined}} value={accessCode} onChange={handleChange} />
            {((error.accessCodeError.required || error.accessCodeError.invalid) === false) &&
              <FormFieldHelperText id="accessCode-error">Enter email verification code received</FormFieldHelperText>}
            <FormFieldHelperText id="accessCode-error" role="alert">{error.accessCodeError.required ? Constants.requiredFieldError : (error.accessCodeError.invalid ? Constants.invalidAccessCodeError : "")}</FormFieldHelperText>
          </FormField>
        </FlexItem>
        <FlexItem style={{height: "2rem"}}>
          <Button sentiment="accented" appearance="bordered"
            onClick={handleSendAccessCode}>
            {Constants.accessCodeButton}
          </Button>
        </FlexItem>
      </StackLayout>
      <PageNavigation backButton={true} previousPage="/forgotPassword" currentPage={Constants.forgotPasswordProvideAccessCode} style={{marginTop: "20%"}} />
      </fieldset>
    </form>
  );
}

export default ForgotPasswordTwoStepVerificationForm;